import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import RichTextEditor from '../../components/common/RichTextEditor';
import MultiFileUpload from '../../components/common/MultiFileUpload';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import {
  getProduct, createProduct, updateProduct, getProductList,
  uploadMainImage, uploadThumbnailImage, uploadDetailImages, deleteDetailImage,
  getMainImageUrl, getThumbnailImageUrl, getDetailImageUrl,
  getOptionImageUrl, uploadOptionImage, markOptionSoldOut, markOptionAvailable,
  getDiscountHistory, createDiscount, deleteDiscount,
  getTranslationList, upsertTranslation, deleteTranslation,
  getSideRecommendList, addSideRecommend, deleteSideRecommend,
} from '../../api/product';
import { getCategoryList } from '../../api/category';
import ImageWithFallback from '../../components/common/ImageWithFallback';
import { formatDateTime } from '../../utils/format';

let optionTempIdSeq = 0;
function newOption() {
  optionTempIdSeq += 1;
  return {
    tempId: `new-${optionTempIdSeq}`, optionId: null, optionName: '', foodtechOptionCode: '', additionalPrice: 0, stockQty: 0, useYn: 'Y',
    soldOutYn: 'N',
    optionImagePath: null, optionImageFile: null, optionImagePreview: null,
  };
}

const DISCOUNT_TYPE_LABEL = { RATE: '비율(%)', AMOUNT: '정액(원)' };

// 다국어 메뉴판(QR오더) 에서 지원하는 언어 - 한국어 원문은 상품 본문 그대로 쓰고 번역만 관리한다.
const LANG_CODE_OPTIONS = [
  { value: 'EN', label: 'English' },
  { value: 'JA', label: '日本語' },
  { value: 'ZH', label: '中文' },
];
function langCodeLabel(code) {
  return LANG_CODE_OPTIONS.find((o) => o.value === code)?.label || code;
}

function formatDiscountValue(discountType, discountValue) {
  if (discountValue == null) return '-';
  return discountType === 'RATE' ? `${discountValue}%` : `${Number(discountValue).toLocaleString()}원`;
}

function getDiscountStatus(discount) {
  const now = new Date();
  const start = new Date(discount.startDate);
  const end = new Date(discount.endDate);
  if (now < start) return { label: '예정', color: 'yellow' };
  if (now > end) return { label: '종료', color: 'gray' };
  return { label: '진행중', color: 'green' };
}

export default function ProductFormPage() {
  const { productId } = useParams();
  const isNew = !productId;
  const navigate = useNavigate();
  const { auth } = useAuth();

  const [form, setForm] = useState({ productName: '', foodtechProductCode: '', basePrice: 0, precautions: '', detailContent: '', recommendYn: 'N', displayOrder: 0 });
  const [options, setOptions] = useState([newOption()]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [categories, setCategories] = useState([]);
  const [selectedLargeId, setSelectedLargeId] = useState('');
  const [selectedMediumId, setSelectedMediumId] = useState('');
  const [selectedSmallId, setSelectedSmallId] = useState('');

  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [thumbnailImageFile, setThumbnailImageFile] = useState(null);
  const [thumbnailImagePreview, setThumbnailImagePreview] = useState(null);
  const [hasMainImage, setHasMainImage] = useState(false);
  const [hasThumbnailImage, setHasThumbnailImage] = useState(false);

  const [existingDetailImages, setExistingDetailImages] = useState([]);
  const [pendingDetailFiles, setPendingDetailFiles] = useState([]);
  const [removeImageTarget, setRemoveImageTarget] = useState(null);

  const [discountHistory, setDiscountHistory] = useState([]);
  const [loadingDiscounts, setLoadingDiscounts] = useState(false);
  const [discountForm, setDiscountForm] = useState({ discountType: 'RATE', discountValue: '', startDate: '', endDate: '' });
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [removeDiscountTarget, setRemoveDiscountTarget] = useState(null);

  const [translations, setTranslations] = useState([]);
  const [loadingTranslations, setLoadingTranslations] = useState(false);
  const [translationForm, setTranslationForm] = useState({ langCode: 'EN', productName: '', detailContent: '' });
  const [savingTranslation, setSavingTranslation] = useState(false);
  const [removeTranslationTarget, setRemoveTranslationTarget] = useState(null);

  // ---- 사이드 메뉴 제안 ----
  const [sideRecommends, setSideRecommends] = useState([]);
  const [loadingSideRecommends, setLoadingSideRecommends] = useState(false);
  const [pickableProducts, setPickableProducts] = useState([]); // 사이드로 추가 가능한 다른 상품 목록
  const [selectedSideProductId, setSelectedSideProductId] = useState('');
  const [savingSideRecommend, setSavingSideRecommend] = useState(false);
  const [removeSideRecommendTarget, setRemoveSideRecommendTarget] = useState(null);

  useEffect(() => {
    getCategoryList().then((res) => setCategories(res.data || [])).catch(() => setCategories([]));
  }, []);

  const fetchProduct = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    setError('');
    try {
      const res = await getProduct(productId);
      const p = res.data;
      setForm({
        productName: p.productName,
        foodtechProductCode: p.foodtechProductCode || '',
        basePrice: p.basePrice,
        precautions: p.precautions || '',
        detailContent: p.detailContent || '',
        recommendYn: p.recommendYn || 'N',
        displayOrder: p.displayOrder ?? 0,
      });
      setOptions((p.options && p.options.length > 0 ? p.options : [{}]).map((o) => ({
        tempId: o.optionId ? `existing-${o.optionId}` : `new-${++optionTempIdSeq}`,
        optionId: o.optionId || null,
        optionName: o.optionName || '',
        foodtechOptionCode: o.foodtechOptionCode || '',
        additionalPrice: o.additionalPrice ?? 0,
        stockQty: o.stockQty ?? 0,
        useYn: o.useYn || 'Y',
        soldOutYn: o.soldOutYn || 'N',
        optionImagePath: o.optionImagePath || null,
        optionImageFile: null,
        optionImagePreview: null,
      })));
      setExistingDetailImages(p.detailImages || []);
      setHasMainImage(!!p.mainImagePath);
      setHasThumbnailImage(!!p.thumbnailImagePath);
      if (p.categoryId) {
        setSelectedSmallId(String(p.categoryId));
      }
    } catch (err) {
      setError(err.response?.data?.message || '상품 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [productId, isNew]);

  useEffect(() => { fetchProduct(); }, [fetchProduct]);

  const fetchDiscounts = useCallback(async () => {
    if (isNew) return;
    setLoadingDiscounts(true);
    try {
      const res = await getDiscountHistory(productId);
      setDiscountHistory(res.data || []);
    } catch {
      setDiscountHistory([]);
    } finally {
      setLoadingDiscounts(false);
    }
  }, [productId, isNew]);

  useEffect(() => { fetchDiscounts(); }, [fetchDiscounts]);

  const fetchTranslations = useCallback(async () => {
    if (isNew) return;
    setLoadingTranslations(true);
    try {
      const res = await getTranslationList(productId);
      setTranslations(res.data || []);
    } catch {
      setTranslations([]);
    } finally {
      setLoadingTranslations(false);
    }
  }, [productId, isNew]);

  useEffect(() => { fetchTranslations(); }, [fetchTranslations]);

  const fetchSideRecommends = useCallback(async () => {
    if (isNew) return;
    setLoadingSideRecommends(true);
    try {
      const res = await getSideRecommendList(productId);
      setSideRecommends(res.data || []);
    } catch {
      setSideRecommends([]);
    } finally {
      setLoadingSideRecommends(false);
    }
  }, [productId, isNew]);

  useEffect(() => { fetchSideRecommends(); }, [fetchSideRecommends]);

  // 사이드로 고를 수 있는 상품 후보 목록 - 자기 자신을 제외한 전체 상품(간단한 셀렉트 박스 구성용).
  useEffect(() => {
    if (isNew) return;
    getProductList({ pageNo: 1, pageSize: 200 })
      .then((res) => setPickableProducts((res.data?.list || []).filter((p) => String(p.productId) !== String(productId))))
      .catch(() => setPickableProducts([]));
  }, [productId, isNew]);

  // 소분류 categoryId 로부터 상위(중/대) 체인을 역추적해 드롭다운을 미리 선택해둔다.
  useEffect(() => {
    if (!selectedSmallId || categories.length === 0 || selectedMediumId) return;
    const small = categories.find((c) => String(c.categoryId) === String(selectedSmallId));
    if (!small) return;
    const medium = categories.find((c) => c.categoryId === small.parentCategoryId);
    if (!medium) return;
    const large = categories.find((c) => c.categoryId === medium.parentCategoryId);
    setSelectedMediumId(String(medium.categoryId));
    if (large) setSelectedLargeId(String(large.categoryId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSmallId, categories]);

  const largeCategories = categories.filter((c) => c.categoryLevel === 1);
  const mediumCategories = categories.filter((c) => c.categoryLevel === 2 && String(c.parentCategoryId) === selectedLargeId);
  const smallCategories = categories.filter((c) => c.categoryLevel === 3 && String(c.parentCategoryId) === selectedMediumId);

  const handleMainImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMainImageFile(file);
    setMainImagePreview(URL.createObjectURL(file));
  };

  const handleThumbnailImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbnailImageFile(file);
    setThumbnailImagePreview(URL.createObjectURL(file));
  };

  const updateOption = (tempId, patch) => {
    setOptions((prev) => prev.map((o) => (o.tempId === tempId ? { ...o, ...patch } : o)));
  };

  const addOption = () => setOptions((prev) => [...prev, newOption()]);
  const removeOption = (tempId) => setOptions((prev) => prev.filter((o) => o.tempId !== tempId));

  const handleOptionImageSelect = (tempId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    updateOption(tempId, { optionImageFile: file, optionImagePreview: URL.createObjectURL(file) });
  };

  /**
   * 옵션 품절 처리/해제. 이미 저장된 옵션(optionId 존재)이면 저장 버튼을 누르지 않아도
   * 즉시 반영되도록 API 를 바로 호출한다 (재고와 무관하게 당장 품절 처리해야 하는
   * 현장 상황을 고려한 것 - 예: 재료 소진). 아직 저장 전인 신규 옵션은 로컬 상태만
   * 바꿔두고, 상품 저장 시 함께 반영된다.
   */
  const handleToggleSoldOut = async (option, checked) => {
    const prevYn = option.soldOutYn;
    const nextYn = checked ? 'Y' : 'N';
    updateOption(option.tempId, { soldOutYn: nextYn });

    if (!option.optionId) return;

    try {
      if (checked) {
        await markOptionSoldOut(option.optionId, auth.adminName);
      } else {
        await markOptionAvailable(option.optionId, auth.adminName);
      }
    } catch (err) {
      updateOption(option.tempId, { soldOutYn: prevYn });
      window.alert(err.response?.data?.message || '품절 처리에 실패했습니다.');
    }
  };

  const handleRemoveExistingImage = async () => {
    try {
      await deleteDetailImage(removeImageTarget.detailImageId);
      setExistingDetailImages((prev) => prev.filter((img) => img.detailImageId !== removeImageTarget.detailImageId));
      setRemoveImageTarget(null);
    } catch (err) {
      window.alert(err.response?.data?.message || '이미지 삭제에 실패했습니다.');
    }
  };

  const handleCreateDiscount = async () => {
    if (!discountForm.discountValue || !discountForm.startDate || !discountForm.endDate) {
      window.alert('할인방식, 값, 시작/종료일시를 모두 입력하세요.');
      return;
    }
    setSavingDiscount(true);
    try {
      await createDiscount(productId, {
        discountType: discountForm.discountType,
        discountValue: Number(discountForm.discountValue),
        // datetime-local 값은 "YYYY-MM-DDTHH:mm" 형식이라 서버가 요구하는 초단위까지 맞춰서 보낸다.
        startDate: `${discountForm.startDate}:00`,
        endDate: `${discountForm.endDate}:00`,
        createdBy: auth.adminName,
      });
      setDiscountForm({ discountType: 'RATE', discountValue: '', startDate: '', endDate: '' });
      fetchDiscounts();
    } catch (err) {
      window.alert(err.response?.data?.message || '할인 등록에 실패했습니다.');
    } finally {
      setSavingDiscount(false);
    }
  };

  const handleRemoveDiscount = async () => {
    try {
      await deleteDiscount(removeDiscountTarget.discountId);
      setRemoveDiscountTarget(null);
      fetchDiscounts();
    } catch (err) {
      window.alert(err.response?.data?.message || '할인 삭제에 실패했습니다.');
    }
  };

  // (productId, langCode) 조합으로 upsert 한다 - 이미 등록된 언어를 다시 저장하면 수정된다.
  const handleSaveTranslation = async () => {
    if (!translationForm.productName.trim()) {
      window.alert('번역된 상품명을 입력하세요.');
      return;
    }
    setSavingTranslation(true);
    try {
      await upsertTranslation(productId, translationForm.langCode, {
        productName: translationForm.productName.trim(),
        detailContent: translationForm.detailContent,
      }, auth.adminName);
      setTranslationForm({ langCode: translationForm.langCode, productName: '', detailContent: '' });
      fetchTranslations();
    } catch (err) {
      window.alert(err.response?.data?.message || '번역 저장에 실패했습니다.');
    } finally {
      setSavingTranslation(false);
    }
  };

  /** 기존 번역 행을 눌러 폼에 불러온다 (저장을 누르면 upsert 라 그대로 수정된다). */
  const handleEditTranslation = (t) => {
    setTranslationForm({ langCode: t.langCode, productName: t.productName, detailContent: t.detailContent || '' });
  };

  const handleRemoveTranslation = async () => {
    try {
      await deleteTranslation(removeTranslationTarget.translationId);
      setRemoveTranslationTarget(null);
      fetchTranslations();
    } catch (err) {
      window.alert(err.response?.data?.message || '번역 삭제에 실패했습니다.');
    }
  };

  const handleAddSideRecommend = async () => {
    if (!selectedSideProductId) {
      window.alert('사이드로 제안할 상품을 선택하세요.');
      return;
    }
    setSavingSideRecommend(true);
    try {
      await addSideRecommend(productId, Number(selectedSideProductId), auth.adminName);
      setSelectedSideProductId('');
      fetchSideRecommends();
    } catch (err) {
      window.alert(err.response?.data?.message || '사이드 메뉴 등록에 실패했습니다.');
    } finally {
      setSavingSideRecommend(false);
    }
  };

  const handleRemoveSideRecommend = async () => {
    try {
      await deleteSideRecommend(removeSideRecommendTarget.sideRecommendId);
      setRemoveSideRecommendTarget(null);
      fetchSideRecommends();
    } catch (err) {
      window.alert(err.response?.data?.message || '사이드 메뉴 삭제에 실패했습니다.');
    }
  };

  const validate = () => {
    if (!form.productName.trim()) return '상품명을 입력하세요.';
    if (options.length === 0) return '선택 옵션을 최소 1개 이상 등록하세요.';
    for (const o of options) {
      if (!o.optionName.trim()) return '모든 옵션의 옵션명을 입력하세요.';
      if (Number(o.stockQty) < 0) return '재고 수량은 0 이상이어야 합니다.';
      if (Number(o.additionalPrice) < 0) return '추가금액은 0 이상이어야 합니다.';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      window.alert(validationError);
      return;
    }

    setSaving(true);
    try {
      const payloadOptions = options.map((o, idx) => ({
        optionId: o.optionId || null,
        optionName: o.optionName,
        foodtechOptionCode: (o.foodtechOptionCode || '').trim() || null,
        additionalPrice: Number(o.additionalPrice),
        stockQty: Number(o.stockQty),
        useYn: o.useYn,
        soldOutYn: o.soldOutYn,
        sortOrder: idx,
      }));

      let targetProductId = productId;

      if (isNew) {
        const res = await createProduct({
          ...form, basePrice: Number(form.basePrice), displayOrder: Number(form.displayOrder) || 0, options: payloadOptions,
          categoryId: selectedSmallId ? Number(selectedSmallId) : null,
          createdBy: auth.adminName,
        });
        targetProductId = res.data.productId;
      } else {
        await updateProduct(productId, {
          ...form, basePrice: Number(form.basePrice), displayOrder: Number(form.displayOrder) || 0, options: payloadOptions,
          categoryId: selectedSmallId ? Number(selectedSmallId) : null,
          updatedBy: auth.adminName,
        });
      }

      if (mainImageFile) {
        await uploadMainImage(targetProductId, mainImageFile, auth.adminName);
      }
      if (thumbnailImageFile) {
        await uploadThumbnailImage(targetProductId, thumbnailImageFile, auth.adminName);
      }
      if (pendingDetailFiles.length > 0) {
        await uploadDetailImages(targetProductId, pendingDetailFiles, auth.adminName);
      }

      // 옵션 이미지는 신규 옵션의 경우 저장 전에는 optionId 를 알 수 없으므로,
      // 상품+옵션 저장이 끝난 뒤 최신 상태를 다시 받아와 실제 optionId 를 확인하고 업로드한다.
      // payloadOptions 를 보낸 순서(=화면에 보이는 options 순서)와 서버가 sort_order 로
      // 내려주는 순서가 같으므로 인덱스로 1:1 대응시킬 수 있다.
      if (options.some((o) => o.optionImageFile)) {
        const refreshed = await getProduct(targetProductId);
        const refreshedOptions = refreshed.data.options || [];
        for (let idx = 0; idx < options.length; idx += 1) {
          const o = options[idx];
          if (!o.optionImageFile) continue;
          const matchedOption = refreshedOptions[idx];
          if (!matchedOption) continue;
          await uploadOptionImage(matchedOption.optionId, o.optionImageFile, auth.adminName);
        }
      }

      navigate('/products');
    } catch (err) {
      window.alert(err.response?.data?.message || '상품 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="상품관리" menuUrl="/admin/product">
        <div className="loading-state">불러오는 중...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="상품관리" menuUrl="/admin/product">
      <div className="page-header">
        <div>
          <h1>{isNew ? '상품 등록' : '상품 수정'}</h1>
          <div className="subtitle">대표 전시상품 정보와 선택 옵션, 이미지를 등록합니다.</div>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/products')}>목록으로</button>
      </div>

      {error && <div className="card card-padded" style={{ marginBottom: 20 }}><span className="text-danger">{error}</span></div>}

      <form onSubmit={handleSubmit}>
        <div className="card card-padded" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, marginBottom: 16 }}>기본 정보</h2>
          <div className="field-row">
            <div className="field" style={{ flex: 2 }}>
              <label>상품명</label>
              <input className="input" value={form.productName} onChange={(e) => setForm({ ...form, productName: e.target.value })} required />
            </div>
            <div className="field">
              <label>푸드테크 상품코드 <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)' }}>(선택)</span></label>
              <input className="input" value={form.foodtechProductCode} onChange={(e) => setForm({ ...form, foodtechProductCode: e.target.value })} placeholder="외부 연동 코드(미입력 시 상품ID 전송)" />
            </div>
            <div className="field">
              <label>기준가격 (원)</label>
              <input className="input" type="number" min="0" value={form.basePrice} onChange={(e) => setForm({ ...form, basePrice: e.target.value })} required />
            </div>
            <div className="field" style={{ flex: '0 0 120px' }}>
              <label>전시순서</label>
              <input className="input" type="number" min="0" value={form.displayOrder}
                     onChange={(e) => setForm({ ...form, displayOrder: e.target.value })}
                     title="값이 작을수록 목록·QR 메뉴 상단에 먼저 노출됩니다" />
            </div>
            <div className="field" style={{ flex: '0 0 auto', justifyContent: 'flex-end' }}>
              <label>추천메뉴</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, height: 34 }}>
                <input
                  type="checkbox"
                  checked={form.recommendYn === 'Y'}
                  onChange={(e) => setForm({ ...form, recommendYn: e.target.checked ? 'Y' : 'N' })}
                />
                QR오더 메뉴 상단 "추천 메뉴"에 노출
              </label>
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>대분류</label>
              <select className="select" value={selectedLargeId} onChange={(e) => { setSelectedLargeId(e.target.value); setSelectedMediumId(''); setSelectedSmallId(''); }}>
                <option value="">선택</option>
                {largeCategories.map((c) => <option key={c.categoryId} value={c.categoryId}>{c.categoryName}</option>)}
              </select>
            </div>
            <div className="field">
              <label>중분류</label>
              <select className="select" value={selectedMediumId} disabled={!selectedLargeId} onChange={(e) => { setSelectedMediumId(e.target.value); setSelectedSmallId(''); }}>
                <option value="">선택</option>
                {mediumCategories.map((c) => <option key={c.categoryId} value={c.categoryId}>{c.categoryName}</option>)}
              </select>
            </div>
            <div className="field">
              <label>소분류</label>
              <select className="select" value={selectedSmallId} disabled={!selectedMediumId} onChange={(e) => setSelectedSmallId(e.target.value)}>
                <option value="">선택</option>
                {smallCategories.map((c) => <option key={c.categoryId} value={c.categoryId}>{c.categoryName}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="card card-padded" style={{ marginBottom: 20 }}>
          <div className="flex-between" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 15 }}>선택 옵션 (추가가격 / 실시간 재고)</h2>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addOption}>+ 옵션 추가</button>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>이미지</th><th>옵션명</th><th>푸드테크코드</th><th>추가가격(원)</th><th>재고수량</th><th>품절</th><th>사용</th><th></th></tr></thead>
              <tbody>
                {options.map((o) => (
                  <tr key={o.tempId}>
                    <td>
                      <ImagePicker
                        size={56}
                        previewUrl={o.optionImagePreview || (o.optionImagePath ? getOptionImageUrl(o.optionId) : null)}
                        onSelect={(e) => handleOptionImageSelect(o.tempId, e)}
                      />
                    </td>
                    <td><input className="input" value={o.optionName} onChange={(e) => updateOption(o.tempId, { optionName: e.target.value })} placeholder="예: 블랙 / L" /></td>
                    <td><input className="input" value={o.foodtechOptionCode} onChange={(e) => updateOption(o.tempId, { foodtechOptionCode: e.target.value })} placeholder="선택" /></td>
                    <td><input className="input" type="number" min="0" value={o.additionalPrice} onChange={(e) => updateOption(o.tempId, { additionalPrice: e.target.value })} /></td>
                    <td><input className="input" type="number" min="0" value={o.stockQty} onChange={(e) => updateOption(o.tempId, { stockQty: e.target.value })} /></td>
                    <td>
                      <label className="sold-out-toggle" title={o.optionId ? '체크하면 즉시 품절 처리됩니다.' : '상품 저장 시 함께 반영됩니다.'}>
                        <input
                          type="checkbox"
                          checked={o.soldOutYn === 'Y'}
                          onChange={(e) => handleToggleSoldOut(o, e.target.checked)}
                        />
                        {o.soldOutYn === 'Y' && <span className="badge badge-red">품절</span>}
                      </label>
                    </td>
                    <td><input type="checkbox" checked={o.useYn === 'Y'} onChange={(e) => updateOption(o.tempId, { useYn: e.target.checked ? 'Y' : 'N' })} /></td>
                    <td>
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => removeOption(o.tempId)} disabled={options.length <= 1}>삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card card-padded" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, marginBottom: 16 }}>기간별 할인</h2>
          {isNew ? (
            <div className="text-secondary" style={{ fontSize: 13 }}>상품을 먼저 저장한 후 할인을 등록할 수 있습니다.</div>
          ) : (
            <>
              <div className="table-wrap" style={{ marginBottom: 16 }}>
                <table className="data-table">
                  <thead>
                    <tr><th>방식</th><th>값</th><th>시작일시</th><th>종료일시</th><th>상태</th><th></th></tr>
                  </thead>
                  <tbody>
                    {loadingDiscounts && (
                      <tr><td colSpan={6} className="text-secondary">불러오는 중...</td></tr>
                    )}
                    {!loadingDiscounts && discountHistory.length === 0 && (
                      <tr><td colSpan={6} className="text-secondary">등록된 할인이 없습니다.</td></tr>
                    )}
                    {!loadingDiscounts && discountHistory.map((d) => {
                      const status = getDiscountStatus(d);
                      return (
                        <tr key={d.discountId}>
                          <td>{DISCOUNT_TYPE_LABEL[d.discountType] || d.discountType}</td>
                          <td>{formatDiscountValue(d.discountType, d.discountValue)}</td>
                          <td>{formatDateTime(d.startDate)}</td>
                          <td>{formatDateTime(d.endDate)}</td>
                          <td><span className={`badge badge-${status.color}`}>{status.label}</span></td>
                          <td>
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => setRemoveDiscountTarget(d)}>삭제</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="field-row" style={{ alignItems: 'flex-end' }}>
                <div className="field">
                  <label>할인방식</label>
                  <select
                    className="select"
                    value={discountForm.discountType}
                    onChange={(e) => setDiscountForm({ ...discountForm, discountType: e.target.value })}
                  >
                    <option value="RATE">비율(%)</option>
                    <option value="AMOUNT">정액(원)</option>
                  </select>
                </div>
                <div className="field">
                  <label>{discountForm.discountType === 'RATE' ? '할인율 (%)' : '할인금액 (원)'}</label>
                  <input
                    className="input"
                    type="number"
                    min="0"
                    value={discountForm.discountValue}
                    onChange={(e) => setDiscountForm({ ...discountForm, discountValue: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>시작일시</label>
                  <input
                    className="input"
                    type="datetime-local"
                    value={discountForm.startDate}
                    onChange={(e) => setDiscountForm({ ...discountForm, startDate: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>종료일시</label>
                  <input
                    className="input"
                    type="datetime-local"
                    value={discountForm.endDate}
                    onChange={(e) => setDiscountForm({ ...discountForm, endDate: e.target.value })}
                  />
                </div>
                <div className="field">
                  <button type="button" className="btn btn-secondary" disabled={savingDiscount} onClick={handleCreateDiscount}>
                    {savingDiscount ? '등록 중...' : '등록'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="card card-padded" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, marginBottom: 16 }}>다국어 메뉴판 (QR오더)</h2>
          {isNew ? (
            <div className="text-secondary" style={{ fontSize: 13 }}>상품을 먼저 저장한 후 번역을 등록할 수 있습니다.</div>
          ) : (
            <>
              <div className="text-secondary" style={{ fontSize: 12.5, marginBottom: 10 }}>
                외국인 손님이 QR오더 화면에서 언어를 선택하면 여기 등록한 번역으로 상품명/상세가 대체됩니다.
                번역을 등록하지 않은 언어는 한국어 원문이 그대로 노출됩니다.
              </div>
              <div className="table-wrap" style={{ marginBottom: 16 }}>
                <table className="data-table">
                  <thead>
                    <tr><th>언어</th><th>번역된 상품명</th><th>상세 번역</th><th></th></tr>
                  </thead>
                  <tbody>
                    {loadingTranslations && (
                      <tr><td colSpan={4} className="text-secondary">불러오는 중...</td></tr>
                    )}
                    {!loadingTranslations && translations.length === 0 && (
                      <tr><td colSpan={4} className="text-secondary">등록된 번역이 없습니다.</td></tr>
                    )}
                    {!loadingTranslations && translations.map((t) => (
                      <tr key={t.translationId}>
                        <td>{langCodeLabel(t.langCode)}</td>
                        <td>{t.productName}</td>
                        <td>{t.detailContent ? '등록됨' : '-'}</td>
                        <td className="actions">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleEditTranslation(t)}>수정</button>
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => setRemoveTranslationTarget(t)}>삭제</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="field-row" style={{ alignItems: 'flex-end' }}>
                <div className="field">
                  <label>언어</label>
                  <select
                    className="select"
                    value={translationForm.langCode}
                    onChange={(e) => setTranslationForm({ ...translationForm, langCode: e.target.value })}
                  >
                    {LANG_CODE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label>번역된 상품명</label>
                  <input
                    className="input"
                    value={translationForm.productName}
                    onChange={(e) => setTranslationForm({ ...translationForm, productName: e.target.value })}
                  />
                </div>
                <div className="field">
                  <button type="button" className="btn btn-secondary" disabled={savingTranslation} onClick={handleSaveTranslation}>
                    {savingTranslation ? '저장 중...' : '저장'}
                  </button>
                </div>
              </div>
              <div className="field" style={{ marginTop: 10 }}>
                <label>상세 번역 (선택 - 비워두면 한국어 상세를 그대로 노출)</label>
                <textarea
                  className="input"
                  rows={3}
                  value={translationForm.detailContent}
                  onChange={(e) => setTranslationForm({ ...translationForm, detailContent: e.target.value })}
                />
              </div>
            </>
          )}
        </div>

        <div className="card card-padded" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, marginBottom: 16 }}>사이드 메뉴 제안 (업셀)</h2>
          {isNew ? (
            <div className="text-secondary" style={{ fontSize: 13 }}>상품을 먼저 저장한 후 사이드 메뉴를 등록할 수 있습니다.</div>
          ) : (
            <>
              <div className="text-secondary" style={{ fontSize: 12.5, marginBottom: 10 }}>
                이 상품을 QR오더 고객 화면에서 장바구니에 담을 때 "이 메뉴와 잘 어울려요" 팝업으로
                아래 등록한 사이드 메뉴를 함께 제안해 객단가를 높입니다.
              </div>
              <div className="table-wrap" style={{ marginBottom: 16 }}>
                <table className="data-table">
                  <thead>
                    <tr><th>순서</th><th>사이드 상품</th><th>가격</th><th></th></tr>
                  </thead>
                  <tbody>
                    {loadingSideRecommends && (
                      <tr><td colSpan={4} className="text-secondary">불러오는 중...</td></tr>
                    )}
                    {!loadingSideRecommends && sideRecommends.length === 0 && (
                      <tr><td colSpan={4} className="text-secondary">등록된 사이드 메뉴가 없습니다.</td></tr>
                    )}
                    {!loadingSideRecommends && sideRecommends.map((r) => (
                      <tr key={r.sideRecommendId}>
                        <td>{r.sortOrder}</td>
                        <td>{r.sideProductName}</td>
                        <td>{Number(r.sideProductBasePrice || 0).toLocaleString()}원</td>
                        <td className="actions">
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => setRemoveSideRecommendTarget(r)}>삭제</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="field-row" style={{ alignItems: 'flex-end' }}>
                <div className="field" style={{ flex: 1 }}>
                  <label>사이드로 제안할 상품</label>
                  <select
                    className="select"
                    value={selectedSideProductId}
                    onChange={(e) => setSelectedSideProductId(e.target.value)}
                  >
                    <option value="">상품 선택</option>
                    {pickableProducts
                      .filter((p) => !sideRecommends.some((r) => String(r.sideProductId) === String(p.productId)))
                      .map((p) => (
                        <option key={p.productId} value={p.productId}>{p.productName}</option>
                      ))}
                  </select>
                </div>
                <div className="field">
                  <button type="button" className="btn btn-secondary" disabled={savingSideRecommend} onClick={handleAddSideRecommend}>
                    {savingSideRecommend ? '추가 중...' : '추가'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="card card-padded" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, marginBottom: 16 }}>이미지</h2>
          <div className="field-row">
            <div className="field">
              <label>대표이미지</label>
              <ImagePicker
                previewUrl={mainImagePreview || (hasMainImage && !isNew ? getMainImageUrl(productId) : null)}
                onSelect={handleMainImageSelect}
              />
            </div>
            <div className="field">
              <label>썸네일이미지</label>
              <ImagePicker
                previewUrl={thumbnailImagePreview || (hasThumbnailImage && !isNew ? getThumbnailImageUrl(productId) : null)}
                onSelect={handleThumbnailImageSelect}
              />
            </div>
          </div>

          <div className="field">
            <label>상세이미지 (최대 10개, 현재 {existingDetailImages.length}개 등록됨)</label>
            {existingDetailImages.length > 0 && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                {existingDetailImages.map((img) => (
                  <div key={img.detailImageId} style={{ position: 'relative' }}>
                    <ImageWithFallback
                      src={getDetailImageUrl(img.detailImageId)}
                      alt="상세이미지"
                      style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--color-border-light)' }}
                    />
                    <button
                      type="button"
                      onClick={() => setRemoveImageTarget(img)}
                      style={{
                        position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%',
                        border: 'none', background: 'var(--color-danger)', color: '#fff', cursor: 'pointer', fontSize: 12, lineHeight: 1,
                      }}
                      aria-label="상세이미지 삭제"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
            <MultiFileUpload
              files={pendingDetailFiles}
              onChange={setPendingDetailFiles}
              accept="image/*"
              maxFiles={Math.max(1, 10 - existingDetailImages.length)}
              maxSizeMB={10}
            />
          </div>
        </div>

        <div className="card card-padded" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, marginBottom: 16 }}>유의사항</h2>
          <RichTextEditor
            value={form.precautions}
            onChange={(html) => setForm({ ...form, precautions: html })}
            placeholder="배송, 교환/환불 등 유의사항을 입력하세요"
            minHeight={140}
          />
        </div>

        <div className="card card-padded" style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, marginBottom: 16 }}>상품상세</h2>
          <RichTextEditor
            value={form.detailContent}
            onChange={(html) => setForm({ ...form, detailContent: html })}
            placeholder="상품 상세 설명을 입력하세요"
            minHeight={240}
          />
        </div>

        <div className="modal-footer" style={{ justifyContent: 'flex-start' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/products')}>취소</button>
        </div>
      </form>

      {removeImageTarget && (
        <ConfirmDialog
          title="상세이미지 삭제"
          message="이 상세이미지를 삭제하시겠습니까?"
          confirmLabel="삭제"
          danger
          onCancel={() => setRemoveImageTarget(null)}
          onConfirm={handleRemoveExistingImage}
        />
      )}

      {removeDiscountTarget && (
        <ConfirmDialog
          title="할인 삭제"
          message="이 할인을 삭제하시겠습니까?"
          confirmLabel="삭제"
          danger
          onCancel={() => setRemoveDiscountTarget(null)}
          onConfirm={handleRemoveDiscount}
        />
      )}

      {removeTranslationTarget && (
        <ConfirmDialog
          title="번역 삭제"
          message={`'${langCodeLabel(removeTranslationTarget.langCode)}' 번역을 삭제하시겠습니까?`}
          confirmLabel="삭제"
          danger
          onCancel={() => setRemoveTranslationTarget(null)}
          onConfirm={handleRemoveTranslation}
        />
      )}

      {removeSideRecommendTarget && (
        <ConfirmDialog
          title="사이드 메뉴 삭제"
          message={`'${removeSideRecommendTarget.sideProductName}' 사이드 메뉴 제안을 삭제하시겠습니까?`}
          confirmLabel="삭제"
          danger
          onCancel={() => setRemoveSideRecommendTarget(null)}
          onConfirm={handleRemoveSideRecommend}
        />
      )}
    </AdminLayout>
  );
}

function ImagePicker({ previewUrl, onSelect, size = 140 }) {
  const inputRef = React.useRef(null);
  const isSmall = size <= 64;
  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        style={{
          width: size, height: size, borderRadius: isSmall ? 6 : 12, border: '1.5px dashed var(--color-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          overflow: 'hidden', background: 'var(--color-surface-alt)',
        }}
      >
        {previewUrl ? (
          <ImageWithFallback src={previewUrl} alt="미리보기" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span className="text-secondary" style={{ fontSize: isSmall ? 9 : 12 }}>{isSmall ? '선택' : '클릭하여 선택'}</span>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={onSelect} />
    </div>
  );
}
